import { useCallback, useEffect, useMemo, useState } from 'react'
import { withAuthRecovery } from '@/lib/authRecovery'
import { supabase } from '@/lib/supabase'
import type { RoyaltyPermission, RoyaltyRole } from '@/types'

const ROLE_PERMISSIONS: Record<RoyaltyRole, readonly RoyaltyPermission[]> = {
  territorial_manager: ['review_assigned'],
  head: ['view', 'manage_accruals', 'manage_payment_requests'],
  accountant: ['view', 'manage_accruals', 'manage_payments', 'manage_payment_requests', 'confirm'],
  super_admin: ['view', 'manage_accruals', 'manage_payments', 'manage_payment_requests', 'confirm', 'admin'],
}

export function hasRoyaltyPermission(
  role: RoyaltyRole | null,
  permission: RoyaltyPermission
): boolean {
  return role ? ROLE_PERMISSIONS[role].includes(permission) : false
}

export function useRoyaltyAccess(userId?: string) {
  const [role, setRole] = useState<RoyaltyRole | null>(null)
  const [loading, setLoading] = useState(Boolean(userId))

  const refresh = useCallback(async () => {
    if (!userId) {
      setRole(null)
      setLoading(false)
      return null
    }

    setLoading(true)
    try {
      const { data, error } = await withAuthRecovery(() =>
        (supabase as any).rpc('get_my_royalty_role')
      )
      if (error) throw error

      const nextRole =
        data === 'territorial_manager' ||
        data === 'head' ||
        data === 'accountant' ||
        data === 'super_admin'
          ? data
          : null
      setRole(nextRole)
      return nextRole
    } catch (error) {
      console.error('Failed to load royalty role:', error)
      setRole(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const permissions = useMemo(
    () => (role ? ROLE_PERMISSIONS[role] : []),
    [role]
  )

  return {
    role,
    loading,
    permissions,
    hasPermission: (permission: RoyaltyPermission) =>
      hasRoyaltyPermission(role, permission),
    refresh,
  }
}
